/* =====================================================================
 * skillCards — 스킬 카드 / 그리드 공용 styled 요소
 *
 * `SkillModal`(채팅 입력창의 스킬 관리 모달)과 마켓플레이스 페이지의
 * 세 탭(내 스킬 · 레퍼런스 · 공유 스킬)이 같은 카드 언어를 쓴다. 모달에서
 * 그대로 옮겨 온 것이라 시각적 변화는 없다 — 색·라운드·간격은 모두
 * 테마 토큰 경유이고, 브랜드 색은 활성 테두리·칩·포커스 링에만 쓴다.
 * ===================================================================== */

import styled, { keyframes } from 'styled-components';
import { Loader2 } from 'lucide-react';
import { Tabs } from '@auw/workspace-ui';
import { workspaceButtonStyles } from '../common/workspaceSurface';

export function isZipSource(filename: string): boolean {
  return filename.toLowerCase().endsWith('.zip');
}

/* ---- Layout ---- */

export const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 28px 28px;
`;

export const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  margin-bottom: 16px;
  border-radius: 8px;
  font-size: 0.75rem;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.error};
  background: ${({ theme }) => theme.colors.errorBg};
`;

export const EmptyState = styled.div`
  padding: 24px 0;
  text-align: center;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* ---- Card Grid ---- */

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
`;

/* ---- Upload Card (DropZone) ---- */

export const UploadCard = styled.div<{ $over: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  border: 1.5px dashed ${({ theme, $over }) =>
    $over ? theme.colors.brand : theme.colors.border};
  background: ${({ theme, $over }) =>
    $over
      ? theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'
      : 'transparent'};
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  overflow: hidden;

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)'};
  }
`;

export const UploadIconZone = styled.div`
  min-height: 84px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const UploadInfoZone = styled.div`
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

export const UploadIconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 4px;
`;

export const UploadLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  text-align: center;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const UploadHint = styled.span`
  font-size: 0.6875rem;
  opacity: 0.55;
  text-align: center;
`;

/* ---- Skill Card ---- */

export const EditNameInput = styled.input`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: transparent;
  border: none;
  border-bottom: 1.5px solid ${({ theme }) => theme.colors.brand};
  outline: none;
  padding: 0 0 2px;
  width: 100%;
  line-height: 1.3;
`;

/* ---- Loading / Empty ---- */

const refSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const RefSpinner = styled(Loader2)`
  animation: ${refSpin} 1s linear infinite;
`;

export const RefLoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const RefEmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.875rem;
`;

/* ---- Ref Card (New Design) ---- */

export const RefCardNew = styled.div<{ $active?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  border: 1px solid ${({ theme, $active }) => $active ? theme.colors.brand : theme.colors.border};
  background: ${({ theme, $active }) => $active ? theme.colors.brandSoft : theme.colors.bgSecondary};
  overflow: hidden;
  padding: 16px;
  gap: 12px;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 4px 16px rgba(0,0,0,0.3)'
        : '0 4px 16px rgba(0,0,0,0.06)'};
  }
`;

export const RefCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 36px;
  flex-shrink: 0;
`;

export const RefAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
`;

export const RefHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

export const RefSkillName = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RefCreator = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.3;
`;

export const RefCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

export const RefDesc = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(0.75rem * 1.5 * 2);
`;

export const RefCatLabel = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: auto;
`;

/* 카드가 좁아지면(모바일 2열·사이드바 옆 좁은 창) 아이콘 줄이 잘리는 대신
 * 아래 줄로 내려간다 — 카드 자체가 overflow:hidden 이라 잘리면 누를 수 없다. */
export const RefCardFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  row-gap: 4px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
  flex-shrink: 0;
`;

export const RefFooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  /* 줄바꿈되어 혼자 남아도 우측 정렬을 유지한다. */
  margin-left: auto;
`;

/* 카드 푸터의 아이콘 버튼. 적용하기 버튼(공통 Button, 32px)과 나란히 서므로
 * 같은 높이로 맞추고, hover 톤은 공통 ghost 버튼과 같은 accent 계열을 쓴다.
 * 툴팁은 공통 `Tooltip` 으로 감싸므로 여기엔 aria-label 만 둔다. */
export const DetailBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  color: ${({ theme }) => theme.colors.textMuted};
  background: transparent;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) => theme.colors.bgHover};
  }
  ${workspaceButtonStyles}
`;

export const DeleteSkillBtn = styled(DetailBtn)`
  &:hover {
    color: ${({ theme }) => theme.colors.error};
    background: ${({ theme }) => theme.colors.errorBg};
  }
`;

export const FavBtn = styled(DetailBtn)<{ $active: boolean }>`
  color: ${({ $active, theme }) =>
    $active ? theme.colors.warning : theme.colors.textMuted};

  &:hover {
    color: ${({ theme }) => theme.colors.warning};
    background: ${({ theme }) => theme.colors.warningBg};
  }
`;

/* 사내 공유 토글. 공유된 상태는 즐겨찾기(FavBtn)와 같은 방식으로 아이콘에
 * 색을 얹되, 브랜드 색을 쓴다 — "공유됨" 칩·활성 테두리와 같은 계열이라
 * 한 카드 안에서 같은 뜻으로 읽힌다. */
export const ShareBtn = styled(DetailBtn)<{ $active: boolean }>`
  color: ${({ $active, theme }) =>
    $active ? theme.colors.brand : theme.colors.textMuted};

  &:hover {
    color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.brandSoft};
  }
`;

/* ---- Search ---- */

export const RefSearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin: 0 28px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)'};
  color: ${({ theme }) => theme.colors.textMuted};

  > svg:first-child { flex-shrink: 0; opacity: 0.5; }
`;

export const RefSearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};

  &::placeholder { color: ${({ theme }) => theme.colors.textMuted}; }
`;

export const RefSearchClear = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  ${workspaceButtonStyles}
`;

/* ---- Skeleton Card ---- */

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

export const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
`;

export const SkeletonIconArea = styled.div`
  aspect-ratio: 4 / 3;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 100%)'
      : 'linear-gradient(90deg, rgba(0,0,0,0.025) 0%, rgba(0,0,0,0.055) 50%, rgba(0,0,0,0.025) 100%)'};
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
`;

export const SkeletonBody = styled.div`
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SkeletonLine = styled.div<{ $width: string; $height: string }>`
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  border-radius: 4px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(90deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.06) 100%)'
      : 'linear-gradient(90deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.07) 50%, rgba(0,0,0,0.04) 100%)'};
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
`;

/* ---- Tabs / Title ---- */

export const SkillTabs = styled(Tabs)`
  padding: 0 20px;
  button { border-radius: 8px 8px 0 0; }
  button:hover { color: ${({ theme }) => theme.colors.brand}; background: ${({ theme }) => theme.colors.brandSoft}; }
  button::after { background: ${({ theme }) => theme.colors.brand}; opacity: 0; }
  button[data-active='true']::after { opacity: 1; }
`;

export const SkillTitleRow = styled.div`display:flex; align-items:center; gap:4px; min-width:0;`;
export const TitleEditBtn = styled(DetailBtn)`width:24px; height:24px; flex-shrink:0;`;
